import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { MonthlySessionDetailDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import {
  SessionsByExperienceDto,
  SessionDetailDto,
} from '@repo/shared/dtos/analytics/SessionDataDto';
import { SessionDetailFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { BASE_URLS, CACHE_TIMES, DEFAULT_QUERY_OPTIONS } from './config';
import {
  fetchAnalyticsData,
  createAnalyticsUrl,
  normalizeAnalyticsFilters,
  createDefaultBaseFilters,
} from './utils';
import { ANALYTICS_CONSTANTS } from '../../components/analytics/constants';

const MAX_SESSION_LOG_MONTHS = ANALYTICS_CONSTANTS.DEFAULT_MONTHLY_LOOKBACK_MONTHS;

interface SessionLogQueryKey {
  month: string;
  filters: SessionDetailFilter;
}

const buildLogQueryKey = (key: SessionLogQueryKey) => ['session-log', key.month, key.filters];

const formatMonthKey = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthsInRange = (start: Date, end: Date): Array<{ year: number; month: number }> => {
  const months: Array<{ year: number; month: number }> = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const limit = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (cursor <= limit) {
    months.push({ year: cursor.getUTCFullYear(), month: cursor.getUTCMonth() + 1 });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return months;
};

const normalizeSessionDate = (value: Date | string): Date | null => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeSessionDetails = (
  sessions: Array<SessionDetailDto | null | undefined>,
): SessionDetailDto[] => {
  const deduped: SessionDetailDto[] = [];
  const seen = new Set<string>();

  sessions.forEach(session => {
    if (!session) {
      return;
    }

    const createdAt = normalizeSessionDate(session.createdAt);
    if (!createdAt) {
      return;
    }

    if (!session.sessionId) {
      return;
    }

    const key = `${session.sessionId}-${createdAt.getTime()}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    deduped.push({ ...session, createdAt });
  });

  return deduped.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
};

export interface SessionLogResult {
  data: SessionDetailDto[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  status: SessionLogStatus;
}

export interface SessionLogOptions {
  enabled?: boolean;
  pageSize?: number;
}

export interface SessionLogStatus {
  hasDateRange: boolean;
  rangeExceedsLimit: boolean;
  monthCount: number;
  maxMonths: number;
  isEnabled: boolean;
  isQueryEnabled: boolean;
}

export const useSessionLog = (
  filters: SessionDetailFilter,
  options?: SessionLogOptions,
): SessionLogResult => {
  const isEnabled = options?.enabled ?? true;

  const defaultFilters = useMemo(() => createDefaultBaseFilters(), []);

  const normalizedFilters: SessionDetailFilter = useMemo(() => {
    return normalizeAnalyticsFilters(filters, defaultFilters) as SessionDetailFilter;
  }, [
    defaultFilters,
    filters?.startDate?.getTime(),
    filters?.endDate?.getTime(),
    filters?.experienceIds?.join(','),
    filters?.experienceTitle,
    filters?.unitIds?.join(','),
    filters?.userId,
    filters?.hasSensorData,
    filters?.granularity,
  ]);

  const hasDateRange = Boolean(normalizedFilters.startDate && normalizedFilters.endDate);

  const months = useMemo(() => {
    if (!normalizedFilters.startDate || !normalizedFilters.endDate) {
      return [];
    }
    return getMonthsInRange(normalizedFilters.startDate, normalizedFilters.endDate);
  }, [normalizedFilters.startDate, normalizedFilters.endDate]);

  const rangeExceedsLimit = months.length > MAX_SESSION_LOG_MONTHS;
  const shouldFetch = isEnabled && hasDateRange && !rangeExceedsLimit;

  const queryConfigs = useMemo(() => {
    if (!shouldFetch) {
      return [];
    }

    return months.map(({ year, month }) => {
      const monthStart = new Date(Date.UTC(year, month - 1, 1));
      const monthEnd = new Date(Date.UTC(year, month, 0, 23, 59, 59));
      const monthFilters: SessionDetailFilter = {
        ...normalizedFilters,
        startDate: monthStart,
        endDate: monthEnd,
      };

      return {
        queryKey: buildLogQueryKey({ month: formatMonthKey(monthStart), filters: monthFilters }),
        queryFn: () =>
          fetchAnalyticsData<MonthlySessionDetailDto>(
            createAnalyticsUrl(
              BASE_URLS.SESSIONS,
              `/details/monthly/${year}/${month}`,
              monthFilters,
            ),
          ),
        staleTime: CACHE_TIMES.STALE_TIME,
        gcTime: CACHE_TIMES.GC_TIME,
        ...DEFAULT_QUERY_OPTIONS,
        enabled: shouldFetch,
      };
    });
  }, [months, normalizedFilters, shouldFetch]);

  const queries = useQueries({
    queries: queryConfigs,
  });

  const isLoading = shouldFetch ? queries.some(query => query.isLoading) : false;
  const error = shouldFetch ? queries.find(query => query.error)?.error || null : null;

  const sessions = useMemo(() => {
    if (!shouldFetch) {
      return [];
    }

    const allSessions = queries.flatMap(query => query.data?.sessions ?? []);

    const normalized = normalizeSessionDetails(allSessions);

    const start = normalizedFilters.startDate?.getTime();
    const end = normalizedFilters.endDate?.getTime();

    if (start == null && end == null) {
      return normalized;
    }

    return normalized.filter(session => {
      const timestamp = session.createdAt.getTime();
      return (start == null || timestamp >= start) && (end == null || timestamp <= end);
    });
  }, [queries, normalizedFilters.startDate, normalizedFilters.endDate, shouldFetch]);

  const refetch = useCallback(() => {
    if (!shouldFetch) {
      return;
    }

    queries.forEach(query => query.refetch());
  }, [queries, shouldFetch]);

  const status: SessionLogStatus = {
    hasDateRange,
    rangeExceedsLimit: hasDateRange && rangeExceedsLimit,
    monthCount: months.length,
    maxMonths: MAX_SESSION_LOG_MONTHS,
    isEnabled,
    isQueryEnabled: shouldFetch,
  };

  return {
    data: sessions,
    isLoading,
    error,
    refetch,
    status,
  };
};

// Re-export SessionsByExperienceDto for consumers that previously imported it via useAnalytics
export type { SessionsByExperienceDto };
